import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EligibilitySessionDto, EligibilitySessionListDto, NphiesEligibilityResultDto } from '@his/shared';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateOnly, dateTime } from '@/lib/utils';
import { Dialog } from '@/components/ui/layout';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Field, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { eligibilityChip, eligibilityPlan, limitKey, pickSession, remainingKey, remainingOf, validityLabel } from './eligibility-session';
import { PolicySearchDialog } from './PolicySearchDialog';

export function useEligibilitySession(patientId: string | null | undefined, payerId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ['eligibility-sessions', patientId, payerId],
    queryFn: () => get<EligibilitySessionListDto>(`/insurance/eligibility/sessions${qs({ patientId, payerId })}`),
    enabled: enabled && Boolean(patientId),
    staleTime: 30_000,
  });
}

export function ExpireEligibilityDialog({ coverageId, onClose, onDone }: { coverageId: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const expire = useMutation({
    mutationFn: () => post(`/insurance/coverages/${coverageId}/eligibility/expire`, reason.trim() ? { reason: reason.trim() } : {}),
    onSuccess: () => {
      toast.success(tr('billing', 'nx_eligibility_expired_now'));
      onDone();
    },
    onError: (caught) => toast.error(tr('billing', 'nx_could_not_expire'), describeError(caught)),
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('billing', 'nx_expire_now_title')}
      description={tr('billing', 'nx_expire_now_description')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('billing', 'cancel')}
          </Button>
          <Button variant="destructive" loading={expire.isPending} onClick={() => expire.mutate()}>
            {tr('billing', 'nx_expire_now')}
          </Button>
        </>
      }
    >
      <Field label={tr('billing', 'reason')}>
        <Textarea rows={2} maxLength={300} value={reason} onChange={(event) => setReason(event.target.value)} />
      </Field>
    </Dialog>
  );
}

export interface EligibilityScope {
  patientId: string;
  payerId: string;
  payerName: string;
  encounterId: string | null;
  admissionId: string | null;
}

export function EligibilityPanel({ scope, canSend }: { scope: EligibilityScope; canSend: boolean }) {
  const { hasModule } = useAuth();
  const sessions = useEligibilitySession(scope.patientId, scope.payerId, hasModule('nphies'));
  if (!hasModule('nphies') || !sessions.data) return null;
  return <EligibilityBody scope={scope} canSend={canSend} items={sessions.data.items} />;
}

function EligibilityBody({ scope, canSend, items }: { scope: EligibilityScope; canSend: boolean; items: EligibilitySessionDto[] }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [expiring, setExpiring] = useState(false);
  const [searching, setSearching] = useState(false);
  const mayAsk = can('nphies.eligibility');
  const mayExpire = can('coverage.verify', 'nphies.eligibility');
  const session = pickSession(items);
  const plan = eligibilityPlan({ hasPolicy: items.length > 0, session });
  const label = validityLabel(session);
  const remaining = session?.state === 'valid' ? remainingOf(session.remainingSeconds) : null;
  const chip = eligibilityChip(session);
  const limit = session?.state === 'valid' ? limitKey(session.limitedBy) : null;

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['eligibility-sessions'] });
    void queryClient.invalidateQueries({ queryKey: ['patient-coverages', scope.patientId] });
    void queryClient.invalidateQueries({ queryKey: ['authorisation'] });
  };

  const check = useMutation({
    mutationFn: () => post<NphiesEligibilityResultDto>('/integrations/nphies/eligibility', { patientId: scope.patientId, coverageId: session?.coverageId, reuseSession: true }),
    onSuccess: (answer) => {
      const detail = answer.warnings.join(' ') || undefined;
      if (answer.eligible) toast.success(answer.sessionReused ? tr('billing', 'nx_session_reused') : answer.message, detail);
      else toast.error(answer.message, detail);
      refresh();
    },
    onError: (caught) => toast.error(tr('billing', 'nx_eligibility_check_failed'), describeError(caught)),
  });
  const authorise = useMutation({
    mutationFn: () =>
      scope.encounterId
        ? post(`/encounters/${scope.encounterId}/authorisation/send`, { payerId: scope.payerId })
        : post('/integrations/nphies/pre-auths/request', { patientId: scope.patientId, payerId: scope.payerId, admissionId: scope.admissionId ?? undefined, send: true }),
    onSuccess: () => {
      toast.success(tr('billing', 'nx_sent_for_authorisation'));
      refresh();
    },
    onError: (caught) => toast.error(tr('billing', 'nx_could_not_authorise'), describeError(caught)),
  });

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center gap-2">
        <CardTitle className="text-sm">{tr('billing', 'nx_eligibility')}</CardTitle>
        <Badge variant={chip.variant}>{tr('billing', chip.key)}</Badge>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {plan.primary === 'discovery' ? (
          <p className="text-muted-foreground">{tr('billing', 'nx_no_policy_for_payer', { payer: scope.payerName })}</p>
        ) : (
          <>
            <p className={session?.state === 'valid' ? 'font-medium' : 'text-muted-foreground'}>
              {label.date ? tr('billing', label.key, { date: dateOnly(label.date) }) : tr('billing', label.key)}
              {remaining && <span className="font-normal text-muted-foreground"> · {tr('billing', remainingKey(remaining.unit), { count: remaining.count })}</span>}
            </p>
            {limit && <p className="text-xs text-muted-foreground">{tr('billing', limit, { days: session?.validDays ?? 0 })}</p>}
            {session?.checkedAt && (
              <p className="text-xs text-muted-foreground">
                {tr('billing', 'nx_checked_at', { when: dateTime(session.checkedAt) })}
                {session.responseId ? ` · ${tr('billing', 'nx_response_id')} ${session.responseId}` : ''}
              </p>
            )}
          </>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          {plan.primary === 'authorise' && mayAsk && (
            <Button size="sm" loading={authorise.isPending} disabled={!canSend} onClick={() => authorise.mutate()}>
              {tr('billing', 'nx_authorise')}
            </Button>
          )}
          {plan.primary === 'check' && mayAsk && session && (
            <Button size="sm" loading={check.isPending} onClick={() => check.mutate()}>
              {tr('billing', 'nx_check_eligibility')}
            </Button>
          )}
          {plan.primary === 'discovery' && mayAsk && (
            <Button size="sm" onClick={() => setSearching(true)}>
              {tr('billing', 'nx_has_insurance_no_policy')}
            </Button>
          )}
          {plan.secondaryDiscovery && mayAsk && (
            <Button size="sm" variant="outline" onClick={() => setSearching(true)}>
              {tr('billing', 'nx_search_for_policy')}
            </Button>
          )}
          {plan.canExpire && mayExpire && session && (
            <Button size="sm" variant="ghost" onClick={() => setExpiring(true)}>
              {tr('billing', 'nx_expire_now')}
            </Button>
          )}
        </div>
        {plan.primary === 'authorise' && !canSend && <Alert>{tr('billing', 'nx_nothing_to_authorise')}</Alert>}
      </CardContent>
      {expiring && session && <ExpireEligibilityDialog coverageId={session.coverageId} onClose={() => setExpiring(false)} onDone={() => { setExpiring(false); refresh(); }} />}
      {searching && (
        <PolicySearchDialog
          patientId={scope.patientId}
          payerId={scope.payerId}
          payerIdsWithPolicy={items.map((item) => item.payerId)}
          onClose={() => setSearching(false)}
          onDone={() => {
            setSearching(false);
            refresh();
          }}
        />
      )}
    </Card>
  );
}
